import React, { createRef } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress
} from '@mui/material';
import StreamRecorder from './StreamRecorder';
import RecordedVideos from './RecordedVideos';

const STATUS_IDLE = 0;
const STATUS_PROCESSING = 1;

export default class StreamWebcam extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      // Streaming state
      stream: null,
      isStreaming: false,
      isProcessing: false,
      error: null,
      
      // Devices state
      availableDevices: [],
      selectedDeviceId: '',
      
      // Patient info
      PatientID: "",
      patientName: "",
      sampleID: "",
      collectionDate: "",
    };

    this.videoRef = createRef();
    this.canvasRef = createRef();
    this.recordedVideosRef = createRef();
  }

  componentDidMount() {
    // Set patient info from props
    const {
      PatientID,
      patientName,
      sampleID,
      collectionDate
    } = this.props;

    this.setState({
      PatientID,
      patientName,
      sampleID,
      collectionDate
    });

    // Get available devices when component mounts
    this.getAvailableDevices();
  }

  componentDidUpdate(prevProps) {
    // Update state if props change
    if (
      this.props.PatientID !== prevProps.PatientID || 
      this.props.patientName !== prevProps.patientName ||
      this.props.sampleID !== prevProps.sampleID || 
      this.props.collectionDate !== prevProps.collectionDate
    ) {
      const {
        PatientID,
        patientName,
        sampleID,
        collectionDate
      } = this.props;
      
      this.setState({
        PatientID,
        patientName,
        sampleID,
        collectionDate
      });
    }
  }

  componentWillUnmount() {
    // Clean up resources
    this.stopStream();
  }

  // Get available camera devices
  getAvailableDevices = async () => {
    try {
      this.setState({ isProcessing: true, error: null });
      
      // Request camera permission before listing devices
      const initialStream = await navigator.mediaDevices.getUserMedia({ video: true });
      
      // Stop the stream immediately after getting permission
      initialStream.getTracks().forEach(track => track.stop());
      
      const devices = await navigator.mediaDevices.enumerateDevices();
      console.log('All devices:', devices);

      // Filter video input devices
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      console.log('Video devices:', videoDevices);

      if (videoDevices.length === 0) {
        this.setState({ 
          error: 'Không tìm thấy thiết bị camera nào',
          isProcessing: false 
        });
        return;
      }

      // Set state with devices and select first device
      this.setState({
        availableDevices: videoDevices,
        selectedDeviceId: videoDevices[0]?.deviceId || '',
        isProcessing: false
      });

    } catch (error) {
      console.error('Error accessing devices:', error);
      this.setState({ 
        error: 'Không thể truy cập thiết bị camera: ' + error.message,
        isProcessing: false 
      });
    }
  };

  // Handle device selection change
  handleDeviceChange = async (event) => {
    const deviceId = event.target.value;
    
    // Stop current stream if streaming
    if (this.state.isStreaming) {
      await this.stopStream();
    }
    
    this.setState({ selectedDeviceId: deviceId });
  };

  // Start streaming from selected device
  startStream = async () => {
    try {
      // If already streaming, stop first
      if (this.state.isStreaming) {
        await this.stopStream();
        
        // Wait a bit for resources to be properly released
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      if (!this.state.selectedDeviceId) {
        this.setState({ error: 'Vui lòng chọn thiết bị camera' });
        return;
      }

      // Set processing state
      this.setState({ isProcessing: true, error: null });

      // Configure video constraints
      const constraints = {
        video: {
          deviceId: { exact: this.state.selectedDeviceId },
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 30 }
        }
      };

      console.log('Using constraints:', constraints);

      // Get media stream
      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      // Set stream to video element
      if (this.videoRef.current) {
        this.videoRef.current.srcObject = stream;
        
        // Ensure video is loaded and playing before proceeding
        await new Promise((resolve) => {
          this.videoRef.current.onloadedmetadata = () => {
            this.videoRef.current.play()
              .then(resolve)
              .catch(err => {
                console.error('Error playing video:', err);
                this.setState({ error: 'Không thể phát video: ' + err.message });
                resolve(); // Resolve anyway to continue
              });
          };
        });
      }

      // Update state
      this.setState({ 
        stream: stream,
        isStreaming: true,
        isProcessing: false,
        error: null 
      });

    } catch (error) {
      console.error('Error accessing camera:', error);
      this.setState({ 
        error: 'Không thể kết nối với camera: ' + error.message,
        isStreaming: false,
        isProcessing: false
      });
    }
  };

  // Stop streaming
  stopStream = async () => {
    console.log('Stopping stream...');
    
    this.setState({ isProcessing: true });
    
    try {
      // Stop all media tracks
      if (this.state.stream) {
        this.state.stream.getTracks().forEach(track => track.stop());
      }
      
      // Clear video source
      if (this.videoRef.current) {
        this.videoRef.current.srcObject = null;
      }
      
      // Update state
      this.setState({
        stream: null,
        isStreaming: false,
        isProcessing: false
      });
      
      console.log('Stream stopped successfully');
    } catch (error) {
      console.error('Error stopping stream:', error);
      this.setState({ 
        error: 'Lỗi khi dừng stream: ' + error.message,
        isProcessing: false
      });
    }
  };

  // Refresh videos list when recording completes
  refreshVideosList = () => {
    console.log('Refreshing videos list...');
    if (this.recordedVideosRef.current && this.recordedVideosRef.current.fetchRecordedVideos) {
      this.recordedVideosRef.current.fetchRecordedVideos();
    }
  };

  render() {
    const {
      isProcessing,
      isStreaming,
      error,
      availableDevices,
      selectedDeviceId,
      stream
    } = this.state;
    
    const { PatientID, patientName, sampleID } = this.props;

    return (
      <div className="space-y-4">
        <Card>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Typography variant="h6" gutterBottom>
                  Camera Stream
                </Typography>
                
                {error && (
                  <Alert severity="error" className="mb-3">
                    {error}
                  </Alert>
                )}
                
                <Box 
                  sx={{ 
                    position: 'relative',
                    aspectRatio: '16/9',
                    backgroundColor: '#000',
                    borderRadius: 1,
                    overflow: 'hidden'
                  }}
                >
                  <video
                    ref={this.videoRef}
                    autoPlay
                    playsInline
                    muted
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                  
                  {isProcessing && (
                    <Box 
                      sx={{ 
                        position: 'absolute', 
                        top: 0, 
                        left: 0, 
                        right: 0, 
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(0,0,0,0.5)'
                      }}
                    >
                      <CircularProgress color="primary" />
                    </Box>
                  )}
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Typography variant="h6" gutterBottom>
                  Cài đặt
                </Typography>
                
                <Box className="space-y-4">
                  <FormControl fullWidth>
                    <InputLabel id="camera-select-label">Chọn Camera</InputLabel>
                    <Select
                      labelId="camera-select-label"
                      id="camera-select"
                      value={selectedDeviceId}
                      label="Chọn Camera"
                      onChange={this.handleDeviceChange}
                      disabled={isProcessing || availableDevices.length === 0 || isStreaming}
                    >
                      {availableDevices.map(device => (
                        <MenuItem key={device.deviceId} value={device.deviceId}>
                          {device.label || `Camera ${availableDevices.indexOf(device) + 1}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <Button
                    variant="contained"
                    color={isStreaming ? "error" : "primary"}
                    onClick={isStreaming ? this.stopStream : this.startStream}
                    disabled={isProcessing || !selectedDeviceId || !PatientID || !patientName || !sampleID}
                    fullWidth
                  >
                    {isProcessing ? 
                      <><CircularProgress size={20} sx={{ mr: 1 }} color="inherit" /> Đang xử lý...</> : 
                      isStreaming ? 'Dừng Camera' : 'Bắt đầu Camera'}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    onClick={this.getAvailableDevices}
                    disabled={isProcessing || isStreaming}
                    fullWidth
                  >
                    Làm mới danh sách camera
                  </Button>
                  
                  <Box className="pt-4">
                    <Typography variant="subtitle2" gutterBottom>
                      Thông tin bệnh nhân:
                    </Typography>
                    <Typography variant="body2">
                      <strong>Mã BN:</strong> {PatientID || 'Chưa nhập'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Tên BN:</strong> {patientName || 'Chưa nhập'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Mã mẫu:</strong> {sampleID || 'Chưa nhập'}
                    </Typography>
                  </Box>
                  
                  <Box className="pt-4">
                    <StreamRecorder 
                      stream={stream}
                      PatientID={PatientID}
                      patientName={patientName}
                      sampleID={sampleID}
                      onRecordingComplete={this.refreshVideosList}
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        
        {/* RecordedVideos component with ref for refreshing */}
        <RecordedVideos
          ref={this.recordedVideosRef}
          PatientID={PatientID}
          patientName={patientName}
          sampleID={sampleID}
        />
      </div>
    );
  }
}